import C from "./Config";
import {
  classifyMedicineTagListResponse,
  MedicineTagListOutcome,
} from "./MedicineTagListResponse";

const BASE_PATH = "/medicineTagList";

// Fetch all active medicine tags (lists) for an institute, with nested active items.
// Returns: { outcome, data: [...tags], message, statusCode }
export async function getByInstitute(instituteId) {
  try {
    const res = await fetch(
      `${C.getUrl()}${BASE_PATH}/getByInstitute?instituteId=${instituteId}`
    );
    const body = await res.json();
    return classifyMedicineTagListResponse(body);
  } catch (error) {
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error fetching medicine lists: ${error.message}`,
      statusCode: null,
    };
  }
}

// Create a new medicine tag (list) with optional initial items.
// Returns: { outcome, data: createdTag, message, statusCode }
export async function createTag(instituteId, tagName, items = [], category = "", tagDescription = "") {
  try {
    const payload = {
      instituteId,
      tagName,
      items,
      category: category || "",
      tagDescription: tagDescription || "",
    };
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/add`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = await res.json();
    const classified = classifyMedicineTagListResponse(body);
    return classified;
  } catch (error) {
    console.error("❌ [createTag] ERROR:", error);
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error creating medicine list: ${error.message}`,
      statusCode: null,
    };
  }
}

// Update an existing medicine tag (list).
// Returns: { outcome, data: updatedTag, message, statusCode }
export async function updateTag(medicineTagId, newTagName, instituteId, category = "", tagDescription = "") {
  try {
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/update`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        medicineTagId,
        tagName: newTagName,
        instituteId,
        category: category || "",
        tagDescription: tagDescription || "",
      }),
    });
    const body = await res.json();
    return classifyMedicineTagListResponse(body);
  } catch (error) {
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error updating medicine list: ${error.message}`,
      statusCode: null,
    };
  }
}

// Deactivate (soft-delete) a medicine tag and all its active items.
// Returns: { outcome, data: null, message, statusCode }
export async function deactivateTag(medicineTagId) {
  try {
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/deactivate/${medicineTagId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    });
    const body = await res.json();
    return classifyMedicineTagListResponse(body);
  } catch (error) {
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error removing medicine list: ${error.message}`,
      statusCode: null,
    };
  }
}

// Add a single medicine item to an existing tag.
// Returns: { outcome, data: createdItem, message, statusCode }
export async function addItem(medicineTagId, medicineId, doseId, whenId, durationValue, duration, instructions = "") {
  try {
    const payload = {
      medicineTagId,
      medicineId,
      doseId,
      whenId,
      durationValue: durationValue ? parseInt(durationValue, 10) : null,
      duration,
      instructions,
    };
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/addItem`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = await res.json();
    const classified = classifyMedicineTagListResponse(body);
    return classified;
  } catch (error) {
    console.error("❌ [addItem] ERROR:", error);
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error adding medicine: ${error.message}`,
      statusCode: null,
    };
  }
}

// Update an existing medicine item.
// Returns: { outcome, data: updatedItem, message, statusCode }
export async function updateItem(medicineTagId, medicineTagDetailId, medicineId, doseId, whenId, durationValue, duration, instructions = "") {
  try {
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/updateItem`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        medicineTagId,
        medicineTagDetailId,
        medicineId,
        doseId,
        whenId,
        durationValue: durationValue ? parseInt(durationValue, 10) : null,
        duration,
        instructions,
      }),
    });
    const body = await res.json();
    return classifyMedicineTagListResponse(body);
  } catch (error) {
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error updating medicine: ${error.message}`,
      statusCode: null,
    };
  }
}

// Deactivate (soft-delete) a single medicine item from a tag.
// Returns: { outcome, data: null, message, statusCode }
export async function deactivateItem(medicineTagDetailId) {
  try {
    const res = await fetch(`${C.getUrl()}${BASE_PATH}/deactivateItem/${medicineTagDetailId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    });
    const body = await res.json();
    return classifyMedicineTagListResponse(body);
  } catch (error) {
    return {
      outcome: MedicineTagListOutcome.ERROR,
      data: null,
      message: `Error removing medicine: ${error.message}`,
      statusCode: null,
    };
  }
}
